import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function BottomInput({ onSend, isLoading, onVoiceStart, mode, userName }) {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState([]); // [{file, preview, type}]
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handleResize = () => {
      const offset = window.innerHeight - vv.height;
      setKeyboardOffset(offset > 50 ? offset : 0);
    };
    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  const handleSend = useCallback(() => {
    if ((text.trim() || attachments.length > 0) && !isLoading) {
      onSend(text.trim(), 'text', attachments);
      setText('');
      setAttachments([]);
    }
  }, [text, attachments, isLoading, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFileSelect = useCallback((files) => {
    const newAttachments = [];
    
    Array.from(files).forEach(file => {
      // Limit file size to 10MB
      if (file.size > 10 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return;
      }
      
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      const isDoc = file.type.includes('document') || file.name.endsWith('.doc') || file.name.endsWith('.docx');
      const isText = file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md');
      
      if (isImage || isPdf || isDoc || isText) {
        const attachment = {
          file,
          name: file.name,
          type: isImage ? 'image' : isPdf ? 'pdf' : isDoc ? 'document' : 'text',
          size: file.size,
        };
        
        // Create preview for images
        if (isImage) {
          attachment.preview = URL.createObjectURL(file);
        }
        
        newAttachments.push(attachment);
      } else {
        alert(`File type not supported: ${file.type || file.name}`);
      }
    });
    
    // Limit to 5 attachments
    setAttachments(prev => {
      const combined = [...prev, ...newAttachments];
      if (combined.length > 5) {
        alert('Maximum 5 attachments allowed');
        return combined.slice(0, 5);
      }
      return combined;
    });
  }, []);

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) {
      handleFileSelect(e.target.files);
    }
    e.target.value = ''; // Reset for same file selection
  };

  const removeAttachment = (index) => {
    setAttachments(prev => {
      const removed = prev[index];
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  // Paste handler for images
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      const files = [];
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      
      if (files.length > 0) {
        e.preventDefault();
        handleFileSelect(files);
      }
    };
    
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleFileSelect]);

  const placeholder = mode === 'agentic'
    ? (userName ? `How can I help ${userName}?` : 'How can I help you?')
    : 'Tell me how you feel...';

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canSend = text.trim() || attachments.length > 0;

  return (
    <motion.div
      data-testid="bottom-input"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        position: 'absolute',
        bottom: 20 + keyboardOffset, left: 12, right: 12, zIndex: 25,
        display: 'flex', flexDirection: 'column', gap: 8,
        transition: 'bottom 0.15s ease-out',
      }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, type: 'spring', stiffness: 200, damping: 22 }}
    >
      {/* Drag overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{
              padding: '32px 48px', borderRadius: 16,
              background: 'rgba(255,255,255,0.1)',
              border: '2px dashed rgba(255,255,255,0.5)',
              color: 'white', fontSize: 18, fontWeight: 500,
            }}>
              Drop files here
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attachment previews */}
      <AnimatePresence>
        {attachments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{
              display: 'flex', gap: 8, flexWrap: 'wrap',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 16,
              backdropFilter: 'blur(8px)',
            }}
          >
            {attachments.map((att, idx) => (
              <motion.div
                key={idx}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                style={{
                  position: 'relative',
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: att.type === 'image' ? 0 : '6px 10px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  maxWidth: 150,
                }}
              >
                {att.type === 'image' && att.preview ? (
                  <img
                    src={att.preview}
                    alt={att.name}
                    style={{
                      width: 60, height: 60,
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <>
                    <span style={{ fontSize: 18 }}>
                      {att.type === 'pdf' ? '📄' : att.type === 'document' ? '📝' : '📎'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: 12, fontWeight: 500,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {att.name}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>
                        {formatFileSize(att.size)}
                      </div>
                    </div>
                  </>
                )}
                <button
                  onClick={() => removeAttachment(idx)}
                  style={{
                    position: 'absolute',
                    top: -6, right: -6,
                    width: 20, height: 20,
                    borderRadius: '50%',
                    background: 'rgba(255,100,100,0.9)',
                    border: 'none',
                    color: 'white',
                    fontSize: 12,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!isLoading && (
          <motion.button
            data-testid="voice-button"
            onClick={onVoiceStart}
            style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.92 }}
          >
            <svg width="36" height="36" viewBox="0 0 48 48">
              <defs>
                <radialGradient id="orbG" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
                  <stop offset="60%" stopColor="rgba(200,180,255,0.25)" />
                  <stop offset="100%" stopColor="rgba(167,21,233,0.08)" />
                </radialGradient>
              </defs>
              <circle cx="24" cy="24" r="16" fill="url(#orbG)" />
              <circle cx="24" cy="24" r="8" fill="rgba(255,255,255,0.2)" />
              <circle cx="24" cy="24" r="4" fill="rgba(255,255,255,0.35)" />
            </svg>
          </motion.button>
        )}

        <div style={{
          flex: 1, display: 'flex', alignItems: 'center',
          height: 48, borderRadius: 24,
          background: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.08)',
          paddingLeft: 18, paddingRight: 6, gap: 6,
        }}>
          {/* Attachment button - only show in agentic mode */}
          {mode === 'agentic' && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt,.md"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              <motion.button
                data-testid="attach-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'transparent',
                  border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isLoading ? 'default' : 'pointer',
                  color: 'rgba(255,255,255,0.5)',
                  flexShrink: 0,
                  transition: 'color 0.2s',
                }}
                whileHover={!isLoading ? { scale: 1.1, color: 'rgba(255,255,255,0.8)' } : {}}
                whileTap={!isLoading ? { scale: 0.9 } : {}}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </motion.button>
            </>
          )}

          <input
            ref={inputRef}
            data-testid="chat-input"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading}
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: 'rgba(255,255,255,0.92)', fontSize: 15, fontWeight: 400, minWidth: 0,
            }}
          />
          <motion.button
            data-testid="send-button"
            onClick={handleSend}
            disabled={!canSend || isLoading}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: canSend ? 'rgba(255,255,255,0.12)' : 'transparent',
              border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: canSend ? 'pointer' : 'default',
              color: canSend ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)',
              flexShrink: 0, transition: 'all 0.2s ease',
            }}
            whileTap={canSend ? { scale: 0.9 } : {}}
          >
            {isLoading ? (
              <motion.div
                style={{
                  width: 16, height: 16, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.2)',
                  borderTopColor: 'rgba(255,255,255,0.7)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
              />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}
